<template>
  <visit-new-modal ref="visitNewModalRef" :newVisitDefaults="newVisitDefaults">
    <template #trigger="slotProps"><span style="display: none"></span></template>
  </visit-new-modal>
  <v-dialog v-model="concurrentReadingDialog" width="auto">
    <v-card>
      <v-card-text>
        {{ $t('mission-planning.warning.already-started-statement', {
        date: concurrentEventDate
      })}}
      </v-card-text>
      <v-card-actions>
        <v-btn @click="concurrentReadingDialog = false">{{ $t('global.btn.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-row class="fill-height">
    <v-col>
      <v-sheet height="64">
        <v-toolbar flat>
          <v-btn
              class="me-4"
              color="grey-darken-2"
              variant="outlined"
              @click="setToday"
          >
            {{$t('planning.filter.label.today')}}
          </v-btn>
          <v-btn
              color="grey-darken-2"
              size="small"
              variant="text"
              icon
              @click="prev"
          >
            <v-icon :icon="mdiChevronLeft" size="small"></v-icon>
          </v-btn>
          <v-btn
              color="grey-darken-2"
              size="small"
              variant="text"
              icon
              @click="next"
          >
            <v-icon :icon="mdiChevronRight" size="small"></v-icon>
          </v-btn>
          <v-toolbar-title v-if="calendar">
            {{ calendar.title }}
          </v-toolbar-title>
          <v-menu location="bottom end">
            <template v-slot:activator="{ props }">
              <v-btn
                  color="grey-darken-2"
                  variant="outlined"
                  v-bind="props"
              >
                <span>{{ $t(`planning.periods.${typeToLabel[type].toLowerCase()}`) }}</span>
                <v-icon :icon="mdiMenuDown" end></v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="type = 'day'">
                <v-list-item-title>{{ $t('planning.periods.day') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'week'">
                <v-list-item-title>{{ $t('planning.periods.week') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'month'">
                <v-list-item-title>{{ $t('planning.periods.month') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar>
      </v-sheet>
      <v-sheet height="600">
        <v-calendar
            ref="calendar"
            v-model="focus"
            :event-color="getEventColor"
            event-overlap-mode="stack"
            :events="missionsPlanningsCalendarData"
            :weekdays="[1, 2, 3, 4, 5]"
            :type="type"
            color="primary"
            @click:date="viewDay"
            @click:more="viewDay"
            @click:event="showEvent"
            @mousedown:time="startTime"
            @mousemove:time="mouseMove"
            @mouseup:time="endDrag"
        >
          <template #event="{ event }">
            <tag-pos-dots
                :tag-pos-ids="event?.tagPos"
                :tag-pos-catalog="tagPosCatalog"
                for-calendar
            />
            <span v-html="event.name"></span>&nbsp;&nbsp;
            <v-icon v-if="event.craStatus === 'approved'" :icon="mdiCheckCircle" size="small"></v-icon>
          </template>
        </v-calendar>
        <v-menu
            v-model="selectedOpen"
            :activator="selectedElement"
            :close-on-content-click="false"
            location="end"
        >
          <v-card
              color="grey-lighten-4"
              min-width="350px"
              flat
          >
            <v-toolbar
                :color="selectedEvent.color"
                dark
            >
              <v-toolbar-title v-html="selectedEvent.name"></v-toolbar-title>
            </v-toolbar>
            <v-card-actions>
              <span v-if="transmissionInProgress">{{ $t('planning.message.transmission-in-progress') }}</span>
              <span v-else-if="magicLinkGenerationFailure">{{ $t('planning.message.no-reading-history') }}</span>
              <v-btn
                  v-else
                  :loading="magicLinkGenerationInProgress"
                  :href="boUrl"
                  target="_blank"
                  :prepend-icon="mdiOpenInNew"
              >
                {{$t('reading.bo-access')}}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script setup>


import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiMenuDown,
  mdiOpenInNew,
  mdiCheckCircle
} from '@mdi/js';
import dateHandler from "@/utils/dateHandler";
import {READING} from "@/constants";
import BOAuthService from "@/services/boAuth";
import VisitNewModal from "@/components/mission/appointments/VisitNewModal.vue";
import TagPosDots from "@/components/common/TagPosDots.vue";

const props = defineProps({
  missionsPlannings: {
    type: Object,
    required: true,
  },
  missions: {
    type: Object,
    required: true,
  },
  pointOfSales: {
    type: Object,
    required: true,
  },
  readingsHistory: {
    type: Object,
    required: true,
  },
  readings: {
    type: Object,
    required: true,
  },
  hasAtLeastOnePlanningTypeFree: {
    type: Boolean,
    required: true,
  },
  planningCras: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  tagPosCatalog: {
    type: Object,
    required: false,
    default: () => ({}),
  }
});

const { t } = useI18n();
const type = ref('week');
const focus = ref('');
const calendar = ref(null)
const concurrentReadingDialog = ref(false);
const concurrentEventDate = ref(null);
const selectedEvent = ref({});
const selectedElement = ref(null);
const selectedOpen = ref(false);
const magicLinkGenerationInProgress = ref(true);
const magicLinkGenerationFailure = ref(false);
const transmissionInProgress = ref(false);
const boUrl = ref('');
const createEvent = ref(null);
const createStart = ref(null);
const extendOriginal = ref(null);
const visitNewModalRef = ref(null);
const newVisitDefaults = ref(null);

const router = useRouter();

const missionPlanningIdsWithReadingInProgressSet = computed(() => new Set(
    Object.values(props.readings)
        .filter(item => item.status === READING.STATUS.NEW)
        .map(item => String(item.mission_planning))
));
const missionPlanningIdsWithReadingSentOrToBeSentSet = computed(() => new Set(
    Object.values(props.readings)
        .filter(item => item.status === READING.STATUS.TO_BE_TRANSMITTED || item.status === READING.STATUS.TRANSMITTED)
        .map(item => String(item.mission_planning))
));

const typeToLabel= {
  month: 'Month',
  week: 'Week',
  day: 'Day',
  '4day': '4 Days',
}

const viewDay = (nativeEvent, { date }) =>{
  focus.value = date
  type.value = 'day'
}

const setToday = () =>{
  focus.value = ''
}

const next = () =>{
  calendar.value.next()
}

const prev = () =>{
  calendar.value.prev()
}

const missionsPlanningsByMission = computed(() => {
  const output = {};

  for (const [mpId, obj] of Object.entries(props.missionsPlannings)) {
    const missionId = obj.mission?.id;
    if (!missionId) continue;

    if (!output[missionId]) {
      output[missionId] = { mp: [] };
    }

    output[missionId].mp.push(mpId);
  }
  return output;
}
);

const missionsPlanningsCalendarData = computed(() => {
  const events = Object.entries(props.missionsPlannings || {}).filter(([mpId, mp])=>{
    return !mp.to_remove;
  }).map(([mpId, mp])=>{
    const mission = props.missions[mp.mission.id];
    const pos = props.pointOfSales[mission.pos.id];
    return {
      id: mpId,
      name: (pos ? pos.name : '-'),
      start: new Date(mp.date_start*1000),
      end: new Date(mp.date_end*1000),
      color: setColor(mp, mpId),
      timed: true,
      isRedirectionToMissionPlanningAllowed: !mp.transmittedReading && !missionPlanningIdsWithReadingSentOrToBeSentSet.value.has(String(mpId)),
      concurrentMissionPlanningReading: Object.entries(props.readings)
            .map(([key, reading]) => {return {id: key, ...reading}})
            .find((iteratedReading) =>
                missionsPlanningsByMission.value[mp?.mission?.id]?.mp?.includes(iteratedReading.mission_planning?.toString()) &&
                mpId?.toString() !== iteratedReading.mission_planning?.toString() &&
                iteratedReading.status === READING.STATUS.NEW
            ) || null,
      transmittedReading: mp.transmittedReading,
      transmissionInProgress: !mp.transmittedReading && missionPlanningIdsWithReadingSentOrToBeSentSet.value.has(String(mpId)),
      validatedEvent: mp.state === '3',
      tagPos: pos?.tag_pos
    };
  });
  if (createEvent.value) events.push(createEvent.value);

  const allowedStatuses = [null, 'request', 'approved'];
  Object.values(props.planningCras || {}).forEach((cra) => {
    if (!allowedStatuses.includes(cra.status)) return;
    const eventType = cra.planning_cra_event_type;
    const isPast = parseInt(cra.date_end) * 1000 < Date.now();
    const color = isPast ? eventType?.color_past : eventType?.color_futur;
    const label = eventType?.label || '';

    const startDate = new Date(parseInt(cra.date_start) * 1000);
    const endDate = new Date(parseInt(cra.date_end) * 1000);
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    if (startDay.getTime() === endDay.getTime()) {
      events.push({
        id: `cra-${cra.id}`,
        name: label,
        start: startDate,
        end: endDate,
        color: color || '#9E9E9E',
        timed: true,
        isPlanningCra: true,
        craStatus: cra.status,
      });
    } else {
      const current = new Date(startDay);
      let segIndex = 0;
      while (current <= endDay) {
        const isFirst = current.getTime() === startDay.getTime();
        const isLast = current.getTime() === endDay.getTime();

        const segStart = isFirst
          ? startDate
          : new Date(current.getFullYear(), current.getMonth(), current.getDate(), 0, 0);
        const segEnd = isLast
          ? endDate
          : new Date(current.getFullYear(), current.getMonth(), current.getDate(), 23, 59);

        events.push({
          id: `cra-${cra.id}-${segIndex}`,
          name: label,
          start: segStart,
          end: segEnd,
          color: color || '#9E9E9E',
          timed: true,
          isPlanningCra: true,
          craStatus: cra.status,
        });

        current.setDate(current.getDate() + 1);
        segIndex++;
      }
    }
  });

  return events;
});

const createLink = async (readingId) => {
  magicLinkGenerationInProgress.value = true;
  const link = await BOAuthService.getMagicLink({'reading_id': readingId});
  if (link.data) {
    boUrl.value = link.data;
  }
  magicLinkGenerationInProgress.value = false;
}

const setColor = (missionPlanning, missionPlanningId) =>{
  if(missionPlanning.state !== '3'){
    return '#DCDCDC'
  }
  else if(missionPlanning.transmittedReading || missionPlanningIdsWithReadingSentOrToBeSentSet.value.has(String(missionPlanningId))){
    return 'green'
  }
  else if(missionPlanningIdsWithReadingInProgressSet.value.has(String(missionPlanningId))){
    return 'orange'
  }
  else if(missionPlanning.event_type === 'visit_rdv'){
    return '#ADD8E6'
  }
  else if(missionPlanning.event_type === 'visit'){
    return '#a9a9a9'
  }
  else {
    return 'black'
  }
}
const getEventColor = (event) =>{
  return event.color
}

const showEvent = (nativeEvent, { event }) => {
  if (event.isPlanningCra) {
    nativeEvent.stopPropagation();
    return;
  }
  magicLinkGenerationFailure.value = false;
  transmissionInProgress.value = false;
  if (!event.isRedirectionToMissionPlanningAllowed) {
    if (!event.transmittedReading && !event.transmissionInProgress) {
      return;
    }
    const open = () => {
      selectedEvent.value = event
      selectedElement.value = nativeEvent.target
      if (event.transmittedReading) {
        createLink(event.transmittedReading.toString());
      }
      else if(event.transmissionInProgress){
        transmissionInProgress.value = true;
      }
      else {
        magicLinkGenerationFailure.value = true;
      }
      requestAnimationFrame(() => requestAnimationFrame(() => selectedOpen.value = true))
    }

    if (selectedOpen.value) {
      selectedOpen.value = false
      requestAnimationFrame(() => requestAnimationFrame(() => open()))
    } else {
      open()
    }
    return;
  }
  if (event.concurrentMissionPlanningReading) {
    concurrentEventDate.value = dateHandler.convertUnixTimestampToHuman(props.missionsPlannings?.[event.concurrentMissionPlanningReading?.mission_planning]?.date_start);
    concurrentReadingDialog.value = true;
    return;
  }

  event.validatedEvent ? goToMissionPlanningPage(event.id) : goToMissionMyVisitPage(event.id);

  nativeEvent.stopPropagation()
}

const goToMissionPlanningPage = (mpId) => {
  router.push({
    name: 'mission-planning-questionnaires',
    params: {
      id: mpId
    },
  });
}

const goToMissionMyVisitPage = (mpId) => {
  router.push({
    name: 'mission-planning-information',
    params: {
      id: mpId
    },
  });
}

const startTime = (nativeEvent, tms) => {
  // In order to avoid creating events when clicking on an existing event
  if (nativeEvent?.target?.className !== "v-calendar-daily__day-interval") {
    return;
  }
  const mouse = toTime(tms)

  if (props.hasAtLeastOnePlanningTypeFree) {
    createStart.value = roundTime(mouse)
    createEvent.value = {
      name: t('appointment.new.modal.title'),
      color: 'blue',
      start: createStart.value,
      end: createStart.value+3600000,
      timed: true,
    }
  }
}

const isSameDay = (ts1, ts2) => {
  const toDate = (ts) => new Date(ts );
  const d1 = toDate(ts1);
  const d2 = toDate(ts2);

  return d1.toDateString() === d2.toDateString();
}

const mouseMove = (nativeEvent, tms) => {
  const mouse = toTime(tms)

  if (createEvent.value && createStart.value !== null) {
    const mouseRounded = roundTime(mouse, false)
    const min = Math.min(mouseRounded, createStart.value)
    const max = Math.max(mouseRounded, createStart.value)

    if (isSameDay(min, max)) {
      createEvent.value.start = min
      createEvent.value.end = max
    }
    else if (isSameDay(mouseRounded, mouseRounded+3600000)) {
      createStart.value = createEvent.value.start = mouseRounded
      createEvent.value.end = mouseRounded+3600000
    }
    else {
      createStart.value = createEvent.value.start = mouseRounded-3600000
      createEvent.value.end = mouseRounded
    }
  }
}

const endDrag = () => {
  if (createEvent.value && !extendOriginal.value) {
    newVisitDefaults.value = {
      start: createEvent.value.start,
      end: createEvent.value.end,
    }
    visitNewModalRef.value?.openModal();
  }
  createEvent.value = null
  createStart.value = null
  extendOriginal.value = null
}

const roundTime = (time, down = true) => {
  const roundTo = 15 // minutes
  const roundDownTime = roundTo * 60 * 1000

  return down
      ? time - time % roundDownTime
      : time + (roundDownTime - (time % roundDownTime))
}

const toTime = (tms) => {
  return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime()
}

onMounted(() => {
  calendar.value.checkChange();
});
</script>